import { apiPost } from '../utils/api';
import { decompressData, prepareUserIdsPayload } from '../utils/compression';

const BATCH_SIZE = 2000;

const unwrap = (response) => (response?.compressed && response.data ? decompressData(response.data) : response);

const isPayloadTooLarge = (error) =>
  error?.statusCode === 413 || /Request Entity Too Large|\b413\b/i.test(error?.message || '');

const normalizeRecord = (record) =>
  record.ggr === undefined && record.gross_gaming_revenue !== undefined
    ? { ...record, ggr: record.gross_gaming_revenue, ngr: record.net_gaming_revenue }
    : record;

const postAnalytics = async (body, token) => {
  const payload = unwrap(await apiPost('analytics', '/analytics', body, token));
  if (!payload?.success) throw new Error(payload?.error || 'Failed to load analytics');
  return payload;
};

/**
 * `fetchAllPlayers` makes the backend load every player on the platform, which is only the real
 * scope for an admin. Everyone else sends their own user IDs (gzipped when the list is long).
 */
const idsPayload = (userIds, fetchAllPlayers) =>
  fetchAllPlayers ? { fetchAllPlayers: true, playerCount: userIds?.length || 0 } : prepareUserIdsPayload(userIds);

/**
 * Hourly analytics records (daily ones past 14 days) for players or for the per-entity rollups that
 * share the same table.
 */
export const fetchAnalyticsRecords = async ({ userIds, fetchAllPlayers = false, start, end, currency, token }) => {
  if (!fetchAllPlayers && !userIds?.length) return [];
  const range = { startDate: start.toISOString(), endDate: end.toISOString(), currency, aggregate: false };

  try {
    const payload = await postAnalytics({ ...idsPayload(userIds, fetchAllPlayers), ...range }, token);
    return (payload.data || []).map(normalizeRecord);
  } catch (error) {
    if (fetchAllPlayers || !isPayloadTooLarge(error) || userIds.length <= BATCH_SIZE) throw error;
    const batches = [];
    for (let i = 0; i < userIds.length; i += BATCH_SIZE) batches.push(userIds.slice(i, i + BATCH_SIZE));
    const payloads = await Promise.all(batches.map((batch) => postAnalytics({ ...idsPayload(batch, false), ...range }, token)));
    return payloads.flatMap((payload) => (payload.data || []).map(normalizeRecord));
  }
};

/** Per-user totals for the period, keyed by username (the API's `aggregate: true` mode). */
export const fetchUserAggregates = async ({ userIds, fetchAllPlayers = false, start, end, currency, token }) => {
  if (!fetchAllPlayers && !userIds?.length) return {};
  const payload = await postAnalytics({
    ...idsPayload(userIds, fetchAllPlayers),
    startDate: start.toISOString(),
    endDate: end.toISOString(),
    currency,
    aggregate: true
  }, token);
  return payload.data || {};
};
